import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { 
  Globe,
  MapPin,
  Users,
  Languages,
  Flag,
  TrendingUp
} from 'lucide-react';

const COLORS = [
  '#10b981', // green (Canada)
  '#3b82f6', // blue (US)
  '#f59e0b', // amber (UK)
  '#ef4444', // red (Australia)
  '#8b5cf6', // violet (Other)
];

/**
 * GeographicDistribution - Shows global access patterns and regional analytics
 * 
 * Features:
 * - Access distribution by country and region
 * - Language preferences
 * - Regional download trends
 * - International usage patterns
 */
const GeographicDistribution = ({ data }) => {
  if (!data) {
    return (
      <div className="text-center py-4 text-gray-500">
        No geographic distribution data available
      </div>
    );
  }

  const chartConfig = {
    downloads: {
      label: "Downloads",
      color: "#3b82f6",
    },
    percentage: {
      label: "Percentage",
      color: "#10b981",
    },
  };

  const languageData = [
    { name: 'English', value: data.languageDistribution.english, color: '#3b82f6' },
    { name: 'French', value: data.languageDistribution.french, color: '#ef4444' },
    { name: 'Other', value: data.languageDistribution.other, color: '#6b7280' },
  ];

  const getCountryFlag = (country) => {
    const flags = {
      'Canada': '🇨🇦',
      'United States': '🇺🇸',
      'United Kingdom': '🇬🇧',
      'Australia': '🇦🇺',
      'Other': '🌍'
    };
    return flags[country] || '🌍';
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const totalDownloads = data.accessByCountry.reduce((sum, country) => sum + country.downloads, 0);

  return (
    <div className="space-y-6">
      {/* Geographic Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Globe className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-500">Countries</span>
            </div>
            <div className="text-2xl font-bold mt-3">{data.accessByCountry.length}</div>
            <p className="text-xs text-gray-500 mt-2">Active regions</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <MapPin className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-gray-500">Top Region</span>
            </div>
            <div className="text-2xl font-bold mt-3">
              {data.topRegions[0]?.region.split(' ')[0] || 'Ontario'}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {formatNumber(data.topRegions[0]?.downloads || 0)} downloads
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Languages className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium text-gray-500">Languages</span>
            </div>
            <div className="text-2xl font-bold mt-3">
              {Object.keys(data.languageDistribution).length}
            </div>
            <p className="text-xs text-gray-500 mt-2">Supported languages</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium text-gray-500">International</span>
            </div>
            <div className="text-2xl font-bold mt-3">
              {data.accessByCountry.find(c => c.country !== 'Canada')?.percentage || 34}%
            </div>
            <p className="text-xs text-gray-500 mt-2">Non-Canadian access</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Country Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Globe className="h-5 w-5" />
              <span>Access by Country</span>
            </CardTitle>
            <CardDescription>
              Geographic distribution of material downloads
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.accessByCountry}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="downloads"
                    nameKey="country"
                  >
                    {data.accessByCountry.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip 
                    formatter={(value, name) => [
                      `${formatNumber(value)} downloads (${Math.round((value / totalDownloads) * 100)}%)`,
                      name
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
            
            {/* Country Legend */}
            <div className="space-y-3 mt-4">
              {data.accessByCountry.map((country, index) => (
                <div key={country.country} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{getCountryFlag(country.country)}</span>
                      <span className="text-sm font-medium">{country.country}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-bold">{formatNumber(country.downloads)}</span>
                    <Badge variant="secondary" className="text-xs">
                      {country.percentage}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Language Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Languages className="h-5 w-5" />
              <span>Language Preferences</span>
            </CardTitle>
            <CardDescription>
              Content language distribution across users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={languageData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    nameKey="name"
                  >
                    {languageData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <ChartTooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
            
            {/* Language Summary */}
            <div className="space-y-3 mt-4">
              {languageData.map((lang) => (
                <div key={lang.name} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: lang.color }}
                    />
                    <span className="text-sm font-medium">{lang.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-bold">{lang.value}%</span>
                    <span className="text-xs text-gray-500">
                      (~{Math.round((lang.value / 100) * totalDownloads / 1000)}K users)
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Language Insights */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start space-x-2">
                <Languages className="h-4 w-4 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">Language Insights</h4>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• French content usage increased 15% this quarter</li>
                    <li>• Quebec region shows 89% French preference</li>
                    <li>• Opportunity for Spanish translation (growing demand)</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Regional Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MapPin className="h-5 w-5" />
            <span>Regional Breakdown</span>
          </CardTitle>
          <CardDescription>
            Top regions by download volume and activity
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.topRegions} layout="horizontal" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis 
                  type="category" 
                  dataKey="region" 
                  tick={{ fontSize: 12 }}
                  width={60}
                />
                <ChartTooltip 
                  content={<ChartTooltipContent />}
                  formatter={(value) => [formatNumber(value), 'Downloads']}
                />
                <Bar 
                  dataKey="downloads" 
                  fill="var(--color-downloads)"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Geographic Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5" />
              <span>Geographic Trends</span>
            </CardTitle>
            <CardDescription>
              Key insights and growth patterns
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 border border-green-200 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-2xl">🇨🇦</span>
                <h4 className="font-medium text-green-900">Canada Leading</h4>
              </div>
              <p className="text-sm text-green-800">
                Canada accounts for {data.accessByCountry.find(c => c.country === 'Canada')?.percentage || 66}% 
                of total downloads, showing strong domestic adoption
              </p>
            </div>

            <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-2xl">🇺🇸</span>
                <h4 className="font-medium text-blue-900">US Growth</h4>
              </div>
              <p className="text-sm text-blue-800">
                United States showing 25% month-over-month growth, 
                indicating expanding international reach
              </p>
            </div>

            <div className="p-4 border border-purple-200 bg-purple-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-2xl">🌍</span>
                <h4 className="font-medium text-purple-900">International</h4>
              </div>
              <p className="text-sm text-purple-800">
                UK and Australia combined represent {
                  (data.accessByCountry.find(c => c.country === 'United Kingdom')?.percentage || 9) +
                  (data.accessByCountry.find(c => c.country === 'Australia')?.percentage || 4)
                }% of downloads, 
                suggesting opportunity for English-speaking markets
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Flag className="h-5 w-5" />
              <span>Localization Opportunities</span>
            </CardTitle>
            <CardDescription>
              Recommendations for expanding global reach
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-amber-900">French Expansion</h4>
                  <p className="text-xs text-amber-700">Increase French materials</p>
                </div>
                <Badge className="bg-amber-100 text-amber-800">High Priority</Badge>
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-green-900">US Market</h4>
                  <p className="text-xs text-green-700">Tailor content for US users</p>
                </div>
                <Badge className="bg-green-100 text-green-800">Medium Priority</Badge>
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-blue-900">Asia Pacific</h4>
                  <p className="text-xs text-blue-700">Explore APAC opportunities</p>
                </div>
                <Badge className="bg-blue-100 text-blue-800">Future Consideration</Badge>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="border-t pt-4 mt-4">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {Math.round((data.accessByCountry.filter(c => c.country !== 'Canada').reduce((sum, c) => sum + c.downloads, 0) / totalDownloads) * 100)}%
                  </div>
                  <p className="text-xs text-gray-500">International Users</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {data.topRegions.length}
                  </div>
                  <p className="text-xs text-gray-500">Active Regions</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GeographicDistribution;